// # Tasks Component for Step 1 Mentorship
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../auth/state';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import cacheManager from '../../../auth/cache_manager';  // # Import cache manager
import { getTargetUserId } from '../m1_access';  // # Import access control utility
import './tasks1.css';
import { FaCheckCircle } from 'react-icons/fa';  // # Import check icon

// # Import step icons
import stepOneIcon from '../../../aneo_media/mentee/one.png';

// # Import chapter icons
import anatomyIcon from '../../../general/library/step1/step1_icons/anatomy.png';
import biochemistryIcon from '../../../general/library/step1/step1_icons/biochemistry.png';
import bloodIcon from '../../../general/library/step1/step1_icons/blood.png';
import cnsIcon from '../../../general/library/step1/step1_icons/cns.png';
import endocrineIcon from '../../../general/library/step1/step1_icons/endocrine.png';
import eyeIcon from '../../../general/library/step1/step1_icons/eye.png';
import gitIcon from '../../../general/library/step1/step1_icons/git.png';
import heartIcon from '../../../general/library/step1/step1_icons/heart.png';
import immunologyIcon from '../../../general/library/step1/step1_icons/immunology.png';
import infectiousIcon from '../../../general/library/step1/step1_icons/infectious.png';
import kidneyIcon from '../../../general/library/step1/step1_icons/kidney.png';
import lungsIcon from '../../../general/library/step1/step1_icons/lungs.png';
import musculoskeletalIcon from '../../../general/library/step1/step1_icons/musculoskeletal.png';
import pathologyIcon from '../../../general/library/step1/step1_icons/pathology.png';
import pharmaIcon from '../../../general/library/step1/step1_icons/pharma.png';
import psychiatryIcon from '../../../general/library/step1/step1_icons/psychiatry.png';
import publicHealthIcon from '../../../general/library/step1/step1_icons/public_health.png';
import reproductiveIcon from '../../../general/library/step1/step1_icons/reproductive-system.png';
import skinIcon from '../../../general/library/step1/step1_icons/skin.png';
import revisionIcon from '../../../general/library/step1/step1_icons/revision.png';

function Tasks1({ isAdmin, isTutor, isMentorView }) {
  const navigate = useNavigate();
  const { userProfile, user } = useAuthStore();
  const [chapterProgress, setChapterProgress] = useState({});
  const [isDarkMode, setIsDarkMode] = useState(false);  // # Add dark mode state
  
  // # Get the correct target user ID based on role context
  const targetUserId = getTargetUserId({
    isAdmin,
    isTutor,
    viewingMenteeId: sessionStorage.getItem('currentViewingMenteeId'),
    currentUserId: user?.uid
  });
  
  // # Create base path based on role context
  const basePath = isAdmin ? '/admin/mentorship' : 
                  isTutor ? '/tutor/mentorship' : 
                  '/mentorship';

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        const db = getFirestore();
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Load chapter progress on component mount
  useEffect(() => {
    const loadChapterProgress = async () => {
      if (!targetUserId) return;
      
      try {
        const db = getFirestore();
        const menteeRef = doc(db, 'db1_mentee', targetUserId);
        const menteeSnapshot = await getDoc(menteeRef);
        
        if (menteeSnapshot.exists()) {
          const menteeData = menteeSnapshot.data();
          const chapters = menteeData.chapters || {};
          
          // # Calculate progress for each chapter
          const progress = {};
          Object.entries(chapters).forEach(([chapterId, chapterData]) => {
            const tasks = chapterData.tasks || {};
            const totalTasks = Object.keys(tasks).length;
            const completedTasks = Object.values(tasks).filter(task => task.done).length;
            
            // # Get the latest task date
            const latestDate = Object.values(tasks)
              .reduce((latest, task) => {
                if (task.date && (!latest || new Date(task.date) > new Date(latest))) {
                  return task.date;
                }
                return latest;
              }, null);
            
            progress[chapterId] = {
              total: totalTasks,
              completed: completedTasks,
              percentage: totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0,
              latestDate: latestDate
            };
          });
          
          setChapterProgress(progress);
        }
      } catch (error) {
        console.error('Error loading chapter progress:', error);
      }
    };
    
    loadChapterProgress();
  }, [targetUserId]);

  // # Define chapters for Step 1 - EXACT match with master database
  const getChapters = () => {
    return [
      { id: 'Biochemistry', title: 'Biochemistry & Genetics', icon: biochemistryIcon },
      { id: 'Immunology', title: 'Immunology', icon: immunologyIcon },
      { id: 'Microbiology', title: 'Microbiology', icon: infectiousIcon },
      { id: 'General Pathology', title: 'General Pathology', icon: pathologyIcon },
      { id: 'Pharmacology', title: 'Pharmacology', icon: pharmaIcon },
      { id: 'Public Health', title: 'Public Health Sciences', icon: publicHealthIcon },
      { id: 'Anatomy', title: 'Anatomy & Histology', icon: anatomyIcon },
      { id: 'Cardiovascular', title: 'Cardiovascular System', icon: heartIcon },
      { id: 'Endocrine', title: 'Endocrine System', icon: endocrineIcon },
      { id: 'Gastrointestinal', title: 'Gastrointestinal System', icon: gitIcon },
      { id: 'Hematology', title: 'Hematology & Oncology', icon: bloodIcon },
      { id: 'Musculoskeletal', title: 'Musculoskeletal & Connective Tissue', icon: musculoskeletalIcon },
      { id: 'Dermatology', title: 'Dermatology', icon: skinIcon },
      { id: 'Neurology', title: 'Neurology', icon: cnsIcon },
      { id: 'Special Senses', title: 'Special Senses', icon: eyeIcon },
      { id: 'Psychiatry', title: 'Behavioral Sciences', icon: psychiatryIcon },
      { id: 'Renal', title: 'Renal System', icon: kidneyIcon },
      { id: 'Reproductive', title: 'Reproductive System', icon: reproductiveIcon },
      { id: 'Respiratory', title: 'Respiratory System', icon: lungsIcon },
      { id: 'Rapid Review', title: 'Rapid Review Series', icon: revisionIcon }
    ];
  };

  // # Handle chapter navigation with role context
  const handleChapterClick = (chapterId) => {
    // # Check if we're in mentor view with a mentee username
    const menteeUsername = sessionStorage.getItem('currentViewingMenteeUsername');
    
    if (isMentorView && menteeUsername) {
      // # Navigate to chapter with username in URL for mentor/tutor view
      const path = isAdmin ? 
        `/mentor/step1/${menteeUsername}/tutora1/chapter/${chapterId}` :
        `/tutor/mentorship/step1/${menteeUsername}/tutora1/chapter/${chapterId}`;
      
      navigate(path, { 
        state: { 
          isAdmin, 
          isTutor,
          isMentorView 
        } 
      });
    } else {
      // # Standard navigation without username
      navigate(`/mentorship/step1/tutora1/chapter/${chapterId}`, { 
        state: { 
          isAdmin, 
          isTutor,
          isMentorView 
        } 
      });
    }
  };

  return (
    <div className={`tasks1_container ${isDarkMode ? 'tasks1_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      <div className="tasks1_main">
        <div className="tasks1_content">
          <div className="tasks1_header">
            <h1>USMLE Step 1 Tasks</h1>
          </div>

          <div className="tasks1_chapters_grid">
            {getChapters().map((chapter) => {
              const progress = chapterProgress[chapter.id] || { total: 0, completed: 0, percentage: 0 };
              
              return (
                <div 
                  key={chapter.id}
                  className="tasks1_chapter_card"
                  onClick={() => handleChapterClick(chapter.id)}
                >
                  <div className="tasks1_chapter_icon">
                    <img src={chapter.icon} alt={chapter.title} />
                  </div>
                  <h3>{chapter.title}</h3>
                  {progress.total > 0 && (
                    <>
                      <div className="tasks1_chapter_progress">
                        <div 
                          className={`tasks1_progress_bar ${progress.completed === progress.total ? 'tasks1_complete' : ''}`}
                          style={{ width: `${progress.percentage}%` }}
                        />
                        <span className="tasks1_progress_text">
                          {progress.completed}/{progress.total} tasks
                          {progress.completed === progress.total && (
                            <FaCheckCircle className="tasks1_check_icon" />
                          )}
                        </span>
                      </div>
                      {progress.latestDate && (
                        <span className="tasks1_date_text">
                          Last updated: {new Date(progress.latestDate).toLocaleDateString()}
                        </span>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default Tasks1;
